import { Component, ElementRef, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, from, map, Observable, take } from 'rxjs';
import {
  changeEditMode,
  createPhotoReportInit,
  getPhotoReportCategoriesInit,
} from 'src/app/data/store/actions/photo-report.action';
import {
  selectCategoriesData,
  selectIsEditMode,
  selectNotFilledCategoriesOfUploadedPhotos,
  selectPhotoReportData,
  selectPhotoReportsListIsLoading,
} from 'src/app/data/store/selectors/photo-report.selectors';
import {
  selectLocalId,
  selectUploadingFilesByCategory,
} from 'src/app/data/store/selectors/uploading-files.selectors';
import {
  Buttons,
  ButtonTextSizes,
  CurrentPlatform,
  FileType,
  ImageProcessingProfile,
  TextSizes,
  TextType,
  ViewType,
} from 'src/app/shared/enums';
import {
  ICreatePhotoReportData,
  IPhotoReport,
  IPhotoReportCategory,
} from 'src/app/shared/services/photo-reports/photo-reports.type';
import { ActionSheetController, AlertController, IonicModule } from '@ionic/angular';
import { selectCurrentPlatform } from 'src/app/data/store/selectors/platform.selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  changeUploadedImageRotate,
  changeUploadedImageRotateSuccess,
  cleanCreatePhotoReportUploadingImages,
} from 'src/app/data/store/actions/uploading-files.actions';
import { IUploadingFile } from 'src/app/shared/services/posts/posts.type';
import { UtilsService } from 'src/app/shared/services/utils.service';
import {
  availableImageFormats,
  cssStyles,
  errorNotificationMessages,
} from 'src/app/shared/constants';
import { AsyncPipe } from '@angular/common';
import { ImageComponent } from '../../../../../../../shared/components/image/image.component';
import { PhotoReportImageItemComponent } from './photo-report-image-item/photo-report-image-item.component';
import { TextComponent } from 'src/app/shared/components/text/text.component';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PostsService } from 'src/app/shared/services/posts/posts.service';
import { isIosDevice } from 'src/app/shared/utils/platform.utils';

@UntilDestroy()
@Component({
  selector: 'app-create-photo-report',
  templateUrl: './create-photo-report.component.html',
  styleUrls: ['./create-photo-report.component.scss'],
  imports: [
    IonicModule,
    TextComponent,
    FormsModule,
    ReactiveFormsModule,
    ImageComponent,
    PhotoReportImageItemComponent,
    ButtonComponent,
    AsyncPipe,
  ],
})
export class CreatePhotoReportComponent implements OnInit {
  private _store = inject(Store);
  private _fb = inject(FormBuilder);
  private alertController = inject(AlertController);
  private actionSheetCtrl = inject(ActionSheetController);
  private _utils = inject(UtilsService);
  private _elRef = inject(ElementRef);
  private _postService = inject(PostsService);

  @ViewChildren('imageInput') imageInputs?: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('cameraInput') cameraInputs?: QueryList<ElementRef<HTMLInputElement>>;

  public currentMode: string = typeof ViewType;

  public currentPhotoReportData: IPhotoReport | null | undefined;

  public currentPhotoReportCategories: string[] | null | undefined;

  public readonly CurrentPlatform = CurrentPlatform;

  public readonly TextSizes = TextSizes;

  public readonly TextType = TextType;

  public readonly Buttons = Buttons;

  public readonly ButtonTextSizes = ButtonTextSizes;

  public readonly availableImageFormats = availableImageFormats;

  public isLoading = false;

  public isImageLoading = false;

  public selectedCategory = '';

  public imageIds: { id: number; localId: number }[] = [];

  public loadedImageIds = new Set<number>();

  public categories: (IPhotoReportCategory & {
    showLeftArrow: boolean;
    showRightArrow: boolean;
  })[] = [];

  public categoriesList$ = this._store.select(selectCategoriesData);

  public uploadingImages$: Observable<Record<string, IUploadingFile[]> | null> | undefined =
    this._store.select(selectUploadingFilesByCategory);

  public currentPlatform$: Observable<CurrentPlatform | null> | undefined =
    this._store.select(selectCurrentPlatform);

  private platform: CurrentPlatform | null = null;

  private _shopId!: number;

  private _uploadedImages: Record<string, IUploadingFile[]> | null = null;

  private readonly _imagesLimit: number = 100;

  public createPhotoReportForm: FormGroup = this._fb.group({
    description: [''],
  });

  private _localId = 0;

  ngOnInit(): void {
    this._store.dispatch(getPhotoReportCategoriesInit());

    this._store
      .select(selectIsEditMode)
      .pipe(untilDestroyed(this))
      .subscribe((currentMode) => {
        if (currentMode && this.currentPhotoReportData) {
          this.createPhotoReportForm.patchValue({
            description: this.currentPhotoReportData.description,
          });
        }
        this.currentMode = currentMode;
      });

    this._store
      .select(selectLocalId)
      .pipe(untilDestroyed(this))
      .subscribe((id: number) => {
        this._localId = id;
      });

    this.uploadingImages$?.pipe(untilDestroyed(this)).subscribe((fileData) => {
      this.currentPhotoReportCategories = fileData ? Object.keys(fileData) : undefined;
      this._uploadedImages = fileData;
      this.isImageLoading = fileData
        ? Object.values(fileData)
            .flat()
            .some((f) => f.isLoading)
        : false;

      if (fileData) {
        this.currentPhotoReportCategories?.forEach((category: any) => {
          fileData[category].forEach((file: IUploadingFile) => {
            if (
              file.id &&
              !file.error &&
              (!this.imageIds.length || !this.imageIds.find((img) => file.id === img.id))
            ) {
              this.imageIds = [...this.imageIds, { id: file.id, localId: file.localId }];
            }
            if (file.id && this._isFileLoaded(file)) {
              this.loadedImageIds.add(file.id);
            }
          });
        });
      }
    });

    this._store
      .select(selectPhotoReportData)
      .pipe(untilDestroyed(this))
      .subscribe((reportData) => (this.currentPhotoReportData = reportData));

    this._store
      .select(selectPhotoReportsListIsLoading)
      .pipe(untilDestroyed(this))
      .subscribe((isLoading) => (this.isLoading = isLoading));

    this.categoriesList$?.subscribe((categories) => {
      this.categories =
        categories?.map((category) => ({
          ...category,
          showLeftArrow: false,
          showRightArrow: true,
        })) || [];
    });

    this.currentPlatform$?.pipe(untilDestroyed(this)).subscribe((p) => (this.platform = p));
  }

  public async openImageSource(categoryName: string) {
    const categoryIndex = this.categories.findIndex((c) => c.categoryName === categoryName);
    if (categoryIndex === -1) return;

    if (this.platform !== CurrentPlatform.mobile || isIosDevice()) {
      this.imageInputs?.get(categoryIndex)?.nativeElement?.click();
      return;
    }
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Добавить фото',
      mode: 'ios',
      buttons: [
        {
          text: 'Сделать фото',
          handler: () => this.cameraInputs?.get(categoryIndex)?.nativeElement?.click(),
        },
        {
          text: 'Выбрать из галереи',
          handler: () => this.imageInputs?.get(categoryIndex)?.nativeElement?.click(),
        },
        {
          text: 'Отмена',
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
  }

  get form() {
    return this.createPhotoReportForm.controls;
  }

  public handleUploadImages(event: any, categoryName: string) {
    this.selectedCategory = categoryName;

    const loadingImagesLength = event.target.files.length;
    let currentFileLimit = this._imagesLimit;
    if (this._uploadedImages && this._uploadedImages[categoryName]) {
      currentFileLimit = this._imagesLimit - this._uploadedImages[categoryName].length;
    }

    if (loadingImagesLength > currentFileLimit) {
      this._utils.openNotification(errorNotificationMessages.photoReportPhotoLimit, true);
    }

    for (
      let i = 0;
      i < (loadingImagesLength <= currentFileLimit ? loadingImagesLength : currentFileLimit);
      i++
    ) {
      this._utils.uploadFile(
        FileType.image,
        event.target.files[i],
        this._localId,
        categoryName,
        true,
        ImageProcessingProfile.photoreport,
      );
      this._localId++;
    }

    (event.target as HTMLInputElement).value = '';
  }

  public async confirmPhotoReportCreation() {
    await this._updateImages();

    const images = this.imageIds
      .sort((a, b) => a.localId - b.localId)
      .map((image, index) => ({ id: image.id, sort: index + 1 }));

    let photoReportData: ICreatePhotoReportData;

    if (this.currentMode === ViewType.editPhotoReport) {
      photoReportData = {
        ...this.currentPhotoReportData,
        images,
      };
      delete photoReportData.imagesData;
    } else {
      photoReportData = {
        shop: {
          id: this._shopId,
        },
        images,
      };
    }

    if (
      (this.form['description'].value && this.currentMode !== ViewType.editPhotoReport) ||
      this.currentMode === ViewType.editPhotoReport
    ) {
      photoReportData = Object.assign(photoReportData, {
        description: this.form['description'].value,
      });
    }

    this._store.dispatch(
      createPhotoReportInit({
        createPhotoReportData: photoReportData,
        currentMode: this.currentMode,
      }),
    );
    this._store.dispatch(changeEditMode({ currentMode: ViewType.createPhotoReport }));
  }

  public handleDeleteChosenItem(localId: number, uploadId?: number, imageCategory?: string) {
    this._utils.handleDeleteChosenImage(localId, imageCategory, uploadId);

    this.imageIds = this.imageIds.filter((img) => img.id !== uploadId);
    if (uploadId) {
      this.loadedImageIds.delete(uploadId);
    }
  }

  public handleImageLoaded(event: { localId: number; id?: number; categoryName: string }): void {
    if (event.id) {
      this.loadedImageIds.add(event.id);
    }
  }

  public areAllImagesLoaded(): boolean {
    if (this.isLoading) {
      return false;
    }
    if (!this.imageIds.length) {
      return false;
    }
    return this.imageIds.every((img) => {
      if (!img.id) {
        return false;
      }
      return this.loadedImageIds.has(img.id);
    });
  }

  public createAlert() {
    if (this.areAllImagesLoaded()) {
      this._store
        .select(selectNotFilledCategoriesOfUploadedPhotos)
        ?.pipe(take(1))
        .subscribe((notFilledCategories) => {
          const notFilledCategoriesArray: string[] | undefined = notFilledCategories?.map((cat) => {
            return cat.categoryValue !== '' ? cat.categoryValue : 'Без категории';
          });

          from(
            this.alertController.create({
              header: `Вы уверены, что хотите ${
                this.currentMode === ViewType.editPhotoReport
                  ? 'сохранить изменения'
                  : 'создать фотоотчёт'
              }?`,
              subHeader: notFilledCategoriesArray?.length
                ? 'У вас остались не заполнены  следующие альбомы: ' +
                  notFilledCategoriesArray.join('; ')
                : undefined,
              mode: 'ios',
              cssClass: 'custom-alert',
              backdropDismiss: false,
              buttons: [
                {
                  text: 'Отменить',
                  role: 'cancel',
                  cssClass: cssStyles.AlertBtn,
                },
                {
                  text: this.currentMode === ViewType.editPhotoReport ? 'Сохранить' : 'Создать',
                  role: 'confirm',
                  cssClass: cssStyles.AlertBtn,
                  handler: () => {
                    this.confirmPhotoReportCreation();
                  },
                },
              ],
            }),
          )
            .pipe(
              take(1),
              map((alert) => alert.present()),
            )
            .subscribe();
        });
    }
  }

  public closeAlert() {
    from(
      this.alertController.create({
        header:
          this.currentMode === ViewType.editPhotoReport
            ? 'Вы уверены, что хотите отменить редактирование фотоотчёта?'
            : 'Вы уверены, что хотите отменить создание фотоотчёта?',
        subHeader:
          this.currentMode === ViewType.editPhotoReport
            ? 'После отмены изменения будут утеряны'
            : 'После отмены фотоотчёт будет утерян',
        mode: 'ios',
        cssClass: 'custom-alert',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Назад',
            role: 'cancel',
            cssClass: 'alert-button',
          },
          {
            text: 'ОК',
            role: 'confirm',
            cssClass: 'alert-button',
            handler: () => {
              this._utils.dismissModal();
              this._store.dispatch(cleanCreatePhotoReportUploadingImages());
              this._store.dispatch(changeEditMode({ currentMode: ViewType.createPhotoReport }));
            },
          },
        ],
      }),
    )
      .pipe(
        take(1),
        map((alert) => alert.present()),
      )
      .subscribe();
  }

  public changeImageRotate(image: IUploadingFile, imageCategory: string) {
    const rotateAngle =
      image.localRotateAngle == null && image.rotateAngle
        ? image.rotateAngle
        : image.localRotateAngle == null
          ? 0
          : image.localRotateAngle;

    const payload = {
      id: image.id!,
      localId: image.localId,
      rotateAngle: rotateAngle === 270 ? 0 : rotateAngle + 90,
      imageCategory: imageCategory,
    };

    this._store.dispatch(
      this.currentMode === ViewType.editPhotoReport
        ? changeUploadedImageRotateSuccess(payload)
        : changeUploadedImageRotate(payload),
    );
  }

  public trackByFn(index: number) {
    return index;
  }

  private scrollTo(id: string, step: number) {
    const container = this._elRef.nativeElement.querySelector('#' + id);
    if (container) {
      const scrollLeft = container.scrollLeft + step;
      const widthDiff = container.scrollWidth - container.clientWidth;

      container.scrollLeft = scrollLeft;

      const categories = [...this.categories];
      const index = categories.findIndex((category) => category.categoryName === id);
      if (index > -1) {
        categories[index].showRightArrow = widthDiff > scrollLeft;
        categories[index].showLeftArrow = scrollLeft > 0 && widthDiff > 0;
        this.categories = categories;
      }
    }
  }

  public scrollLeft(id: string) {
    this.scrollTo(id, -150);
  }

  public scrollRight(id: string) {
    this.scrollTo(id, 150);
  }

  private _isFileLoaded(file: IUploadingFile) {
    return (
      file.id &&
      ((file.uploadedFileData && file.uploadedFileData.loaded === true) ||
        (!file.uploadedFileData && this.currentMode === ViewType.editPhotoReport))
    );
  }

  private async _updateImages() {
    if (this.uploadingImages$ && this.currentMode === ViewType.editPhotoReport) {
      try {
        this.isLoading = true;

        const promises: Promise<unknown>[] = [];
        const categories = await firstValueFrom(this.uploadingImages$);

        Object.values(categories || {}).forEach((images) => {
          images.forEach((image) => {
            console.log({ image });
            if (
              image.localRotateAngle === 0
                ? image.rotateAngle != null
                : image.localRotateAngle != null && image.rotateAngle !== image.localRotateAngle
            ) {
              promises.push(
                firstValueFrom(
                  this._postService.changeImageRotateAngle(image.id!, image.localRotateAngle),
                ),
              );
            }
          });
        });

        await Promise.all(promises);
      } catch {
        // empty
      }
    }
  }
}
